// components/PrivacyPolicy.jsx
import React from 'react';
import '../styles/PrivacyPolicy.css';
import Navbar from '../component/Navbar';
import Footer from '../component/Footer';

const PrivacyPolicy = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="privacy-policy-page">
      <Navbar/>
      <div className="privacy-policy-header">
        <div className="privacy-policy-header-bg">
          <div className="privacy-policy-header-circle privacy-policy-header-circle-1"></div>
          <div className="privacy-policy-header-circle privacy-policy-header-circle-2"></div>
        </div>
        <div className="privacy-policy-header-content">
          <h1 className="privacy-policy-title">Privacy Policy</h1>
          <p className="privacy-policy-subtitle">
            Your privacy matters to us. Learn how we collect, use, and protect your information.
          </p>
          <p className="privacy-policy-date">Last Updated: January 1, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="privacy-policy-container">
        <div className="privacy-policy-content">
          {/* Introduction */}
          <section className="privacy-policy-section">
            <h2 className="privacy-policy-section-title">1. Introduction</h2>
            <p className="privacy-policy-section-text">
              At <strong>WaterDamagePro</strong>, we respect your privacy and are committed to protecting 
              your personal information. This Privacy Policy explains how we collect, use, disclose, 
              and safeguard your information when you visit our website or use our services.
            </p>
            <p className="privacy-policy-section-text">
              Please read this Privacy Policy carefully. By using our website or services, you 
              consent to the collection and use of your information as described in this policy.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="privacy-policy-section">
            <h2 className="privacy-policy-section-title">2. Information We Collect</h2>
            <p className="privacy-policy-section-text">
              We may collect the following types of information:
            </p>
            <div className="privacy-policy-list">
              <div className="privacy-policy-list-item">
                <span className="privacy-policy-list-icon">📋</span>
                <div>
                  <h4>Personal Information</h4>
                  <p>Name, email address, phone number, and service address provided by you.</p>
                </div>
              </div>
              <div className="privacy-policy-list-item">
                <span className="privacy-policy-list-icon">🌐</span>
                <div>
                  <h4>Usage Data</h4>
                  <p>Information about how you interact with our website, including pages visited and time spent.</p>
                </div>
              </div>
              <div className="privacy-policy-list-item">
                <span className="privacy-policy-list-icon">📱</span>
                <div>
                  <h4>Device Information</h4>
                  <p>IP address, browser type, operating system, and device type.</p>
                </div>
              </div>
              <div className="privacy-policy-list-item">
                <span className="privacy-policy-list-icon">🍪</span>
                <div>
                  <h4>Cookies</h4>
                  <p>Small data files stored on your device to enhance your browsing experience.</p>
                </div>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="privacy-policy-section">
            <h2 className="privacy-policy-section-title">3. How We Use Your Information</h2>
            <p className="privacy-policy-section-text">
              We use the information we collect to:
            </p>
            <ul className="privacy-policy-ul">
              <li>Provide and maintain our services</li>
              <li>Process your service requests and bookings</li>
              <li>Communicate with you about your service needs</li>
              <li>Improve our website and services</li>
              <li>Send you promotional offers and updates (with your consent)</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          {/* How We Share Your Information */}
          <section className="privacy-policy-section">
            <h2 className="privacy-policy-section-title">4. How We Share Your Information</h2>
            <p className="privacy-policy-section-text">
              We may share your information with:
            </p>
            <ul className="privacy-policy-ul">
              <li>
                <strong>Service Providers:</strong> Licensed restoration professionals who provide 
                the services you request.
              </li>
              <li>
                <strong>Third-Party Partners:</strong> Trusted partners who assist us in operating 
                our website and services.
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law or to protect our rights.
              </li>
            </ul>
            <p className="privacy-policy-section-text">
              We do not sell, rent, or trade your personal information to third parties for 
              marketing purposes without your explicit consent.
            </p>
          </section>

          {/* Data Security */}
          <section className="privacy-policy-section">
            <h2 className="privacy-policy-section-title">5. Data Security</h2>
            <p className="privacy-policy-section-text">
              We implement appropriate technical and organizational measures to protect your 
              personal information from unauthorized access, disclosure, alteration, or destruction.
            </p>
            <div className="privacy-policy-security-badges">
              <span className="privacy-policy-security-badge">
                🔒 SSL Encryption
              </span>
              <span className="privacy-policy-security-badge">
                🛡️ Secure Servers
              </span>
              <span className="privacy-policy-security-badge">
                🔐 Data Protection
              </span>
              <span className="privacy-policy-security-badge">
                ✅ Regular Audits
              </span>
            </div>
          </section>

          {/* Cookies */}
          <section className="privacy-policy-section">
            <h2 className="privacy-policy-section-title">6. Cookies</h2>
            <p className="privacy-policy-section-text">
              We use cookies to enhance your experience on our website. Cookies are small files 
              that are stored on your device and help us understand how you use our site.
            </p>
            <p className="privacy-policy-section-text">
              You can control cookies through your browser settings. However, disabling cookies 
              may affect your ability to use certain features of our website.
            </p>
            <div className="privacy-policy-cookie-types">
              <div className="privacy-policy-cookie-type">
                <h4>Essential Cookies</h4>
                <p>Necessary for the website to function properly.</p>
              </div>
              <div className="privacy-policy-cookie-type">
                <h4>Analytics Cookies</h4>
                <p>Help us understand how visitors interact with our website.</p>
              </div>
              <div className="privacy-policy-cookie-type">
                <h4>Preference Cookies</h4>
                <p>Remember your preferences for a better experience.</p>
              </div>
            </div>
          </section>

          {/* Your Rights */}
          <section className="privacy-policy-section">
            <h2 className="privacy-policy-section-title">7. Your Rights</h2>
            <p className="privacy-policy-section-text">
              You have the right to:
            </p>
            <ul className="privacy-policy-ul">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt-out of marketing communications</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p className="privacy-policy-section-text">
              To exercise any of these rights, please contact us using the information provided below.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="privacy-policy-section">
            <h2 className="privacy-policy-section-title">8. Children's Privacy</h2>
            <p className="privacy-policy-section-text">
              Our services are not intended for children under 13 years of age. We do not knowingly 
              collect personal information from children under 13. If you are a parent or guardian 
              and believe your child has provided us with personal information, please contact us 
              immediately.
            </p>
          </section>

          {/* Changes to This Policy */}
          <section className="privacy-policy-section">
            <h2 className="privacy-policy-section-title">9. Changes to This Policy</h2>
            <p className="privacy-policy-section-text">
              We may update this Privacy Policy from time to time. We will notify you of any changes 
              by posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>
            <p className="privacy-policy-section-text">
              We encourage you to review this Privacy Policy periodically to stay informed about 
              how we protect your information.
            </p>
          </section>

          {/* Contact Us */}
          <section className="privacy-policy-section privacy-policy-contact">
            <h2 className="privacy-policy-section-title">10. Contact Us</h2>
            <p className="privacy-policy-section-text">
              If you have any questions, concerns, or requests regarding this Privacy Policy, 
              please contact us:
            </p>
            <div className="privacy-policy-contact-info">
              <div className="privacy-policy-contact-item">
                <span>📞</span>
                <div>
                  <strong>Phone:</strong>
                  <a href="tel:+1 (833) 493-6236">+1 (833) 493-6236</a>
                </div>
              </div>
              <div className="privacy-policy-contact-item">
                <span>✉️</span>
                <div>
                  <strong>Email:</strong>
                  <a href="mailto:info@waterdamagepro.com">info@waterdamagepro.com</a>
                </div>
              </div>
              <div className="privacy-policy-contact-item">
                <span>📍</span>
                <div>
                  <strong>Address:</strong>
                  <span>WaterDamagePro, USA</span>
                </div>
              </div>
            </div>
          </section>

          {/* Back to Top */}
          <button className="privacy-policy-back-top" onClick={scrollToTop}>
            ↑ Back to Top
          </button>
        </div>
      </div>
      <Footer/>
    </div>
  );
};

export default PrivacyPolicy;